const PACKET_MAGIC=0x31435454; // `TTC1` en little-endian.
const FULL_SNAPSHOT_KIND=1;
const DELTA_SNAPSHOT_KIND=2;
const MATCH_INPUT_KIND=3;
const MAX_COLLECTION_ITEMS=1024;
const MAX_STRING_BYTES=65_535;
const MAX_TAIL_BYTES=4*1024*1024;

const encoder=new TextEncoder();
const decoder=new TextDecoder('utf-8',{fatal:true});

const VEHICLE_FLOAT_FIELDS=Object.freeze([
  'x','y','z','vx','vy','vz','yaw','pitch','roll',
  'qx','qy','qz','qw','angularX','angularY','angularZ',
  'uprightness','groundHeight','nitro','forwardSpeed','lateralSpeed',
  'skidAmount','steer','throttle','distance','topSpeedMultiplier',
  'airTime','lastAirTime','impactCooldown','impactPitchVelocity',
  'impactYawVelocity','impactRollVelocity','jumpBufferTimer',
  'groundedGraceTimer','airDodgeWindowTimer','airDodgeSpinTimer',
  'airDodgeImpactTimer','health','respawnTimer','invulnerableTimer',
  'chaseInstability','chasePressureHold','chaseHitCooldown','chaseSlowTimer',
  'chaseNitroLockTimer','ruptureFuses','ruptureInstability',
  'ruptureRespawnTimer','ruptureReachedPlatform','aimX','aimY','aimZ',
  'powerupHeld','powerupPending','powerupRevealTimer','nitroOverchargeTimer',
  'powerupShieldTimer','homingAmmoTimer',
]);

const VEHICLE_FLAG_FIELDS=Object.freeze([
  'grounded','wheelsGrounded','boosting','airDodgeAvailable','disabled','ruptureEliminated',
]);

const PROJECTILE_FLOAT_FIELDS=Object.freeze([
  'x','y','z','vx','vy','vz','radius','life','launchSurfaceY',
]);

const INPUT_AXIS_FIELDS=Object.freeze([
  'throttle','accelerator','steer','airPitch','airYaw','airRoll','aimX','aimY','aimZ',
]);

const INPUT_FLAG_FIELDS=Object.freeze(['jump','boost','stabilize','handbrake','fire','powerup']);

function byteView(value){
  if(value instanceof ArrayBuffer)return new Uint8Array(value);
  if(ArrayBuffer.isView(value))return new Uint8Array(value.buffer,value.byteOffset,value.byteLength);
  throw new TypeError('El paquete binario debe ser un ArrayBuffer o una vista tipada.');
}

class Writer{
  constructor(initialSize=2048){
    this.bytes=new Uint8Array(initialSize);
    this.view=new DataView(this.bytes.buffer);
    this.offset=0;
  }
  ensure(length){
    const required=this.offset+length;
    if(required<=this.bytes.length)return;
    let size=this.bytes.length;
    while(size<required)size*=2;
    const next=new Uint8Array(size);
    next.set(this.bytes);
    this.bytes=next;
    this.view=new DataView(next.buffer);
  }
  u8(value){this.ensure(1);this.view.setUint8(this.offset,value);this.offset+=1;}
  i16(value){this.ensure(2);this.view.setInt16(this.offset,value,true);this.offset+=2;}
  u16(value){this.ensure(2);this.view.setUint16(this.offset,value,true);this.offset+=2;}
  u32(value){this.ensure(4);this.view.setUint32(this.offset,value>>>0,true);this.offset+=4;}
  f32(value){this.ensure(4);this.view.setFloat32(this.offset,value,true);this.offset+=4;}
  f64(value){this.ensure(8);this.view.setFloat64(this.offset,value,true);this.offset+=8;}
  raw(value){this.ensure(value.byteLength);this.bytes.set(value,this.offset);this.offset+=value.byteLength;}
  string(value){
    const bytes=encoder.encode(String(value??''));
    if(bytes.byteLength>MAX_STRING_BYTES)throw new RangeError('Un string del paquete excede el máximo permitido.');
    this.u16(bytes.byteLength);
    this.raw(bytes);
  }
  finish(){return this.bytes.slice(0,this.offset);}
}

class Reader{
  constructor(value){
    this.bytes=byteView(value);
    this.view=new DataView(this.bytes.buffer,this.bytes.byteOffset,this.bytes.byteLength);
    this.offset=0;
  }
  ensure(length){if(this.offset+length>this.bytes.byteLength)throw new RangeError('Paquete binario truncado.');}
  u8(){this.ensure(1);const value=this.view.getUint8(this.offset);this.offset+=1;return value;}
  i16(){this.ensure(2);const value=this.view.getInt16(this.offset,true);this.offset+=2;return value;}
  u16(){this.ensure(2);const value=this.view.getUint16(this.offset,true);this.offset+=2;return value;}
  u32(){this.ensure(4);const value=this.view.getUint32(this.offset,true);this.offset+=4;return value;}
  f32(){this.ensure(4);const value=this.view.getFloat32(this.offset,true);this.offset+=4;return value;}
  f64(){this.ensure(8);const value=this.view.getFloat64(this.offset,true);this.offset+=8;return value;}
  raw(length){this.ensure(length);const value=this.bytes.subarray(this.offset,this.offset+length);this.offset+=length;return value;}
  string(){return decoder.decode(this.raw(this.u16()));}
  done(){return this.offset===this.bytes.byteLength;}
}

function finiteFloat(value,fallback=0){
  if(value===null||value===undefined)return fallback;
  const number=Number(value);
  return Number.isFinite(number)?number:fallback;
}

function packetKind(value){
  try{
    const bytes=byteView(value);
    if(bytes.byteLength<5)return 0;
    const magic=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength).getUint32(0,true);
    return magic===PACKET_MAGIC?bytes[4]:0;
  }catch{return 0;}
}

function writeHeader(writer,kind){writer.u32(PACKET_MAGIC);writer.u8(kind);}

function readHeader(reader,kind){
  if(reader.u32()!==PACKET_MAGIC||reader.u8()!==kind)throw new TypeError('Paquete binario desconocido.');
}

function writeCount(writer,items,label){
  if(!Array.isArray(items))throw new TypeError(`${label} debe ser un array.`);
  if(items.length>MAX_COLLECTION_ITEMS)throw new RangeError(`${label} excede el máximo permitido.`);
  writer.u16(items.length);
}

function readCount(reader,label,maximum=MAX_COLLECTION_ITEMS){
  const count=reader.u16();
  if(count>maximum)throw new RangeError(`Cantidad de ${label} inválida.`);
  return count;
}

function vehicleFlags(vehicle){
  let flags=0;
  VEHICLE_FLAG_FIELDS.forEach((field,index)=>{if(vehicle?.[field])flags|=1<<index;});
  return flags;
}

function wireFloat(vehicle,field){
  const fallback=field==='respawnTimer'?Number.NaN:0;
  return Math.fround(finiteFloat(vehicle?.[field],fallback));
}

function writeVehicleBody(writer,vehicle){
  writer.u32(Math.max(0,finiteFloat(vehicle.ack))>>>0);
  writer.u8(vehicleFlags(vehicle));
  VEHICLE_FLOAT_FIELDS.forEach((field)=>writer.f32(wireFloat(vehicle,field)));
}

function readVehicleBody(reader,id){
  const vehicle={id,ack:reader.u32()};
  const flags=reader.u8();
  VEHICLE_FLAG_FIELDS.forEach((field,index)=>{vehicle[field]=Boolean(flags&(1<<index));});
  VEHICLE_FLOAT_FIELDS.forEach((field)=>{
    const value=reader.f32();
    vehicle[field]=field==='respawnTimer'&&!Number.isFinite(value)?null:finiteFloat(value);
  });
  return vehicle;
}

function writeVehicle(writer,vehicle){writer.string(vehicle.id);writeVehicleBody(writer,vehicle);}
function readVehicle(reader){const id=reader.string();return readVehicleBody(reader,id);}

function writeProjectile(writer,projectile){
  writer.string(projectile.id);
  writer.string(projectile.ownerId);
  writer.string(projectile.cannonId);
  writer.string(projectile.projectileId);
  writer.string(projectile.homingTargetId||'');
  writer.u32(Math.max(0,finiteFloat(projectile.sourceSeq))>>>0);
  writer.u8((projectile.launchGrounded?1:0)|(projectile.guided?2:0));
  PROJECTILE_FLOAT_FIELDS.forEach((field)=>writer.f32(finiteFloat(projectile[field])));
}

function readProjectile(reader){
  const projectile={
    id:reader.string(),ownerId:reader.string(),cannonId:reader.string(),projectileId:reader.string(),
    homingTargetId:reader.string(),sourceSeq:reader.u32(),
  };
  const flags=reader.u8();
  projectile.launchGrounded=Boolean(flags&1);
  projectile.guided=Boolean(flags&2);
  PROJECTILE_FLOAT_FIELDS.forEach((field)=>{projectile[field]=finiteFloat(reader.f32());});
  return projectile;
}

function writeSnapshotTail(writer,snapshot){
  const tail=encoder.encode(JSON.stringify({
    events:Array.isArray(snapshot.events)?snapshot.events:[],
    matchState:snapshot.matchState??null,
  }));
  if(tail.byteLength>MAX_TAIL_BYTES)throw new RangeError('Los eventos y el estado del snapshot exceden el máximo permitido.');
  writer.u32(tail.byteLength);
  writer.raw(tail);
}

function readSnapshotTail(reader){
  const length=reader.u32();
  if(length>MAX_TAIL_BYTES)throw new RangeError('Sección variable del snapshot demasiado grande.');
  const tail=JSON.parse(decoder.decode(reader.raw(length)));
  return {events:Array.isArray(tail?.events)?tail.events:[],matchState:tail?.matchState??null};
}

function assertSnapshot(snapshot){
  if(snapshot?.type!=='match:snapshot')throw new TypeError('Solo se pueden codificar mensajes match:snapshot.');
}

export function isBinarySnapshotPacket(value){
  const kind=packetKind(value);
  return kind===FULL_SNAPSHOT_KIND||kind===DELTA_SNAPSHOT_KIND;
}

export function isBinaryMatchSnapshot(value){return packetKind(value)===FULL_SNAPSHOT_KIND;}
export function isBinaryMatchInput(value){return packetKind(value)===MATCH_INPUT_KIND;}

export function encodeMatchSnapshot(snapshot){
  assertSnapshot(snapshot);
  const players=snapshot.players||[];
  const projectiles=snapshot.projectiles||[];
  const writer=new Writer();
  writeHeader(writer,FULL_SNAPSHOT_KIND);
  writer.string(snapshot.matchId);
  writer.f64(finiteFloat(snapshot.serverTime));
  writer.u32(Math.max(0,finiteFloat(snapshot.tick))>>>0);
  writeCount(writer,players,'players');
  players.forEach((vehicle)=>writeVehicle(writer,vehicle));
  writeCount(writer,projectiles,'projectiles');
  projectiles.forEach((projectile)=>writeProjectile(writer,projectile));
  writeSnapshotTail(writer,snapshot);
  return writer.finish();
}

export function decodeMatchSnapshot(value){
  const reader=new Reader(value);
  readHeader(reader,FULL_SNAPSHOT_KIND);
  const matchId=reader.string();
  const serverTime=reader.f64();
  const tick=reader.u32();
  const players=Array.from({length:readCount(reader,'jugadores')},()=>readVehicle(reader));
  const projectiles=Array.from({length:readCount(reader,'proyectiles')},()=>readProjectile(reader));
  const tail=readSnapshotTail(reader);
  if(!reader.done())throw new RangeError('El snapshot binario contiene bytes inesperados.');
  return {type:'match:snapshot',matchId,serverTime,tick,players,projectiles,...tail};
}

function buildVehicleChange(previous,current){
  if(!previous)return {id:current.id,full:true,vehicle:current};
  let lowMask=0;
  let highMask=0;
  const values=[];
  VEHICLE_FLOAT_FIELDS.forEach((field,index)=>{
    const before=wireFloat(previous,field);
    const after=wireFloat(current,field);
    if(Object.is(before,after)||(Number.isNaN(before)&&Number.isNaN(after)))return;
    if(index<32)lowMask|=1<<index;else highMask|=1<<(index-32);
    values.push(after);
  });
  const beforeFlags=vehicleFlags(previous);
  const afterFlags=vehicleFlags(current);
  const flagsChanged=beforeFlags!==afterFlags;
  const ack=Math.max(0,finiteFloat(current.ack))>>>0;
  const ackChanged=ack!==(Math.max(0,finiteFloat(previous.ack))>>>0);
  if(!lowMask&&!highMask&&!flagsChanged&&!ackChanged)return null;
  return {id:current.id,full:false,ackChanged,ack,flagsChanged,flags:afterFlags,lowMask:lowMask>>>0,highMask:highMask>>>0,values};
}

function writeVehicleChange(writer,change){
  writer.string(change.id);
  writer.u8(change.full?1:0);
  if(change.full){writeVehicleBody(writer,change.vehicle);return;}
  let meta=0;
  if(change.ackChanged)meta|=1;
  if(change.flagsChanged)meta|=2;
  writer.u8(meta);
  if(change.ackChanged)writer.u32(change.ack);
  if(change.flagsChanged)writer.u8(change.flags);
  writer.u32(change.lowMask);
  writer.u32(change.highMask);
  change.values.forEach((value)=>writer.f32(value));
}

function readVehicleChange(reader){
  const id=reader.string();
  const full=Boolean(reader.u8());
  if(full)return {id,full,vehicle:readVehicleBody(reader,id)};
  const meta=reader.u8();
  const ackChanged=Boolean(meta&1);
  const flagsChanged=Boolean(meta&2);
  const ack=ackChanged?reader.u32():0;
  const flags=flagsChanged?reader.u8():0;
  const lowMask=reader.u32();
  const highMask=reader.u32();
  const values=[];
  VEHICLE_FLOAT_FIELDS.forEach((field,index)=>{
    const mask=index<32?lowMask:highMask;
    const bit=1<<(index<32?index:index-32);
    if(mask&bit)values.push(reader.f32());
  });
  return {id,full:false,ackChanged,ack,flagsChanged,flags,lowMask,highMask,values};
}

export function encodeMatchSnapshotDelta(previous,snapshot){
  assertSnapshot(previous);
  assertSnapshot(snapshot);
  if(previous.matchId!==snapshot.matchId)throw new TypeError('Los snapshots delta deben pertenecer a la misma partida.');
  const previousPlayers=new Map((previous.players||[]).map((player)=>[player.id,player]));
  const currentPlayers=snapshot.players||[];
  const currentIds=new Set(currentPlayers.map((player)=>player.id));
  const removed=(previous.players||[]).filter((player)=>!currentIds.has(player.id)).map((player)=>player.id);
  const changes=currentPlayers.map((player)=>buildVehicleChange(previousPlayers.get(player.id),player)).filter(Boolean);
  const projectiles=snapshot.projectiles||[];
  const writer=new Writer();
  writeHeader(writer,DELTA_SNAPSHOT_KIND);
  writer.string(snapshot.matchId);
  writer.f64(finiteFloat(snapshot.serverTime));
  writer.u32(Math.max(0,finiteFloat(snapshot.tick))>>>0);
  writer.u32(Math.max(0,finiteFloat(previous.tick))>>>0);
  writeCount(writer,removed,'removed players');
  removed.forEach((id)=>writer.string(id));
  writeCount(writer,changes,'player changes');
  changes.forEach((change)=>writeVehicleChange(writer,change));
  writeCount(writer,projectiles,'projectiles');
  projectiles.forEach((projectile)=>writeProjectile(writer,projectile));
  writeSnapshotTail(writer,snapshot);
  return writer.finish();
}

export function decodeMatchSnapshotDelta(value){
  const reader=new Reader(value);
  readHeader(reader,DELTA_SNAPSHOT_KIND);
  const matchId=reader.string();
  const serverTime=reader.f64();
  const tick=reader.u32();
  const baseTick=reader.u32();
  const removedPlayerIds=Array.from({length:readCount(reader,'jugadores eliminados')},()=>reader.string());
  const playerChanges=Array.from({length:readCount(reader,'cambios de jugadores')},()=>readVehicleChange(reader));
  const projectiles=Array.from({length:readCount(reader,'proyectiles')},()=>readProjectile(reader));
  const tail=readSnapshotTail(reader);
  if(!reader.done())throw new RangeError('El delta binario contiene bytes inesperados.');
  return {type:'match:snapshot-delta',matchId,serverTime,tick,baseTick,removedPlayerIds,playerChanges,projectiles,...tail};
}

export function decodeSnapshotPacket(value){
  const kind=packetKind(value);
  if(kind===FULL_SNAPSHOT_KIND)return decodeMatchSnapshot(value);
  if(kind===DELTA_SNAPSHOT_KIND)return decodeMatchSnapshotDelta(value);
  throw new TypeError('El paquete no es un snapshot binario.');
}

export function applyMatchSnapshotDelta(previous,delta){
  assertSnapshot(previous);
  if(delta?.type!=='match:snapshot-delta'||previous.matchId!==delta.matchId||previous.tick!==delta.baseTick){
    throw new RangeError('El snapshot delta no coincide con su base.');
  }
  const removed=new Set(delta.removedPlayerIds||[]);
  const players=(previous.players||[]).filter((player)=>!removed.has(player.id)).map((player)=>({...player}));
  const byId=new Map(players.map((player,index)=>[player.id,index]));
  (delta.playerChanges||[]).forEach((change)=>{
    if(change.full){
      const index=byId.get(change.id);
      if(index===undefined){byId.set(change.id,players.length);players.push({...change.vehicle});}
      else players[index]={...change.vehicle};
      return;
    }
    const index=byId.get(change.id);
    if(index===undefined)throw new RangeError(`Falta la base del jugador ${change.id}.`);
    const vehicle=players[index];
    if(change.ackChanged)vehicle.ack=change.ack;
    if(change.flagsChanged)VEHICLE_FLAG_FIELDS.forEach((field,flagIndex)=>{vehicle[field]=Boolean(change.flags&(1<<flagIndex));});
    let valueIndex=0;
    VEHICLE_FLOAT_FIELDS.forEach((field,fieldIndex)=>{
      const mask=fieldIndex<32?change.lowMask:change.highMask;
      const bit=1<<(fieldIndex<32?fieldIndex:fieldIndex-32);
      if(!(mask&bit))return;
      const value=change.values[valueIndex++];
      vehicle[field]=field==='respawnTimer'&&!Number.isFinite(value)?null:finiteFloat(value);
    });
  });
  return {
    type:'match:snapshot',matchId:delta.matchId,serverTime:delta.serverTime,tick:delta.tick,
    players,projectiles:delta.projectiles||[],events:delta.events||[],matchState:delta.matchState??null,
  };
}

function quantizeAxis(value){
  return Math.round(Math.max(-1,Math.min(1,finiteFloat(value)))*32767);
}

export function encodeMatchInput(message){
  if(message?.type!=='match:input')throw new TypeError('Solo se pueden codificar mensajes match:input.');
  const controls=message.controls||{};
  const contacts=(Array.isArray(message.contacts)?message.contacts:[]).slice(0,3);
  const writer=new Writer(256);
  writeHeader(writer,MATCH_INPUT_KIND);
  writer.string(message.matchId);
  writer.u32(Math.max(0,finiteFloat(message.seq))>>>0);
  writer.f64(finiteFloat(message.estimatedServerTime));
  writer.f32(finiteFloat(message.clientRttMs));
  writer.f32(finiteFloat(message.remoteViewDelayMs));
  INPUT_AXIS_FIELDS.forEach((field)=>writer.i16(quantizeAxis(controls[field])));
  let flags=0;
  INPUT_FLAG_FIELDS.forEach((field,index)=>{if(controls[field])flags|=1<<index;});
  writer.u8(flags);
  writer.u8(contacts.length);
  contacts.forEach((contact)=>{
    writer.string(contact.targetId);
    writer.f64(finiteFloat(contact.contactTime));
    ['actorX','actorY','actorZ','targetX','targetY','targetZ'].forEach((field)=>writer.f32(finiteFloat(contact[field])));
  });
  return writer.finish();
}

export function decodeMatchInput(value){
  const reader=new Reader(value);
  readHeader(reader,MATCH_INPUT_KIND);
  const matchId=reader.string();
  const seq=reader.u32();
  const estimatedServerTime=reader.f64();
  const clientRttMs=reader.f32();
  const remoteViewDelayMs=reader.f32();
  const controls={};
  INPUT_AXIS_FIELDS.forEach((field)=>{controls[field]=Math.max(-1,reader.i16()/32767);});
  const flags=reader.u8();
  INPUT_FLAG_FIELDS.forEach((field,index)=>{controls[field]=Boolean(flags&(1<<index));});
  const contactCount=reader.u8();
  if(contactCount>3)throw new RangeError('Cantidad de contactos inválida.');
  const contacts=Array.from({length:contactCount},()=>{
    const contact={targetId:reader.string(),contactTime:reader.f64()};
    ['actorX','actorY','actorZ','targetX','targetY','targetZ'].forEach((field)=>{contact[field]=reader.f32();});
    return contact;
  });
  if(!reader.done())throw new RangeError('El input binario contiene bytes inesperados.');
  return {type:'match:input',matchId,seq,controls,estimatedServerTime,clientRttMs,remoteViewDelayMs,contacts};
}

export const BINARY_SNAPSHOT_FLOAT_FIELDS=VEHICLE_FLOAT_FIELDS;
export const BINARY_INPUT_AXIS_FIELDS=INPUT_AXIS_FIELDS;
